import { useEffect, useState } from "react";
import {
    ClipboardCheck,
    Plus,
    FileText,
    StickyNote,
    Upload,
    ClipboardPaste,
    Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import SupervisorNotesList from "./SupervisorNotesList";
import ChangeDocumentsTimeline from "./ChangeDocumentsTimeline";
import ChangeDocumentUploadDialog from "./ChangeDocumentUploadDialog";
import SupervisorNotesImportDialog from "./SupervisorNotesImportDialog";
import AddSupervisorNoteDialog from "./AddSupervisorNoteDialog";
import AddExternalLinkDialog from "./AddExternalLinkDialog";
import { Button } from "./ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Iter-60 (renamed iter-62 PART 6 → "Changes") wrapper used by
 * SceneDetail and CharacterDetail. Surfaces both the parsed text notes
 * AND uploaded change documents (PDF/DOCX/images) for this scene or
 * character.
 *
 * Iter-98 — Add-note parity with `SupervisorNotesPage`. Owners +
 * admins can: add a single text note (auto-linked to the current
 * scene/character context), paste a batch, or upload a document.
 * Edit/Delete on individual notes and documents continues to live
 * inside `SupervisorNotesList` and `ChangeDocumentsTimeline`.
 */
export default function SupervisorNotesSection({
    projectId,
    sceneId,
    characterId,
    isOwner, // Iter-156D — write-capable role (Owner/Admin/Contributor).
             // Controls the Add CTA visibility + "Add text note" item.
    canAdmin = false, // Iter-156D — Owner/Admin only. Controls the
             // Paste / Upload items AND the per-row Edit/Delete
             // admin override inside SupervisorNotesList /
             // ChangeDocumentsTimeline. Defaults to false so older
             // call-sites (none expected) degrade safely.
    showSceneRef = true,
    showCharacterRef = true,
    testid = "supervisor-notes-section",
    title = "Notes",
    emptyHint = "No notes yet for this view.",
}) {
    const apiBase = useApiBase();
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [pasteOpen, setPasteOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    // Iter-156E — External link parity. Same admin-only gate as
    // paste / upload; defaults to the contextual scene / character.
    const [linkOpen, setLinkOpen] = useState(false);
    const [notesRefreshKey, setNotesRefreshKey] = useState(0);
    const [docsRefreshKey, setDocsRefreshKey] = useState(0);
    // iter-62 PART 9 follow-up: load scenes + characters lazily so the
    // upload + add dialogs' scene/character selects are populated even
    // when the user is on a Scene/Character detail page that doesn't
    // ship those arrays itself.
    const [scenes, setScenes] = useState([]);
    const [characters, setCharacters] = useState([]);
    const needLazyLoad = uploadOpen || addNoteOpen;
    useEffect(() => {
        if (!needLazyLoad || !projectId) return;
        if (scenes.length || characters.length) return;
        let aborted = false;
        (async () => {
            try {
                const [{ data: sc }, { data: ch }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}/scenes`),
                    api.get(`${apiBase}/${projectId}/characters`),
                ]);
                if (aborted) return;
                setScenes(sc || []);
                setCharacters(ch || []);
            } catch {
                /* non-fatal: dialogs still work without dropdown options */
            }
        })();
        return () => {
            aborted = true;
        };
    }, [needLazyLoad, projectId, scenes.length, characters.length]);

    const createNote = async (payload) => {
        try {
            await api.post(`${apiBase}/${projectId}/supervisor-notes`, payload);
            toast.success("Note added");
            setNotesRefreshKey((v) => v + 1);
        } catch (err) {
            const msg = formatApiError(err);
            toast.error(msg);
            // re-throw so the dialog stays open and shows the error.
            throw err;
        }
    };

    return (
        <section
            data-testid={testid}
            className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4 mt-6 mb-6"
        >
            <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-xs uppercase tracking-[0.25em] text-violet-200 flex items-center gap-2">
                    <ClipboardCheck size={12} />
                    {title}
                </h2>
                {isOwner ? (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                type="button"
                                data-testid={`${testid}-add-trigger`}
                                variant="secondary"
                                size="sm"
                                className="h-8 px-2.5 text-[11px] font-semibold"
                            >
                                <Plus size={12} className="mr-1" />
                                Add note
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="w-72"
                            data-testid={`${testid}-add-menu`}
                        >
                            <DropdownMenuItem
                                data-testid={`${testid}-add-text`}
                                onSelect={() => setAddNoteOpen(true)}
                                className="cursor-pointer flex-col items-start gap-0.5 py-2"
                            >
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <StickyNote size={14} />
                                    Add text note
                                </div>
                                <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                    A single note auto-linked to this view.
                                </div>
                            </DropdownMenuItem>
                            {canAdmin ? (
                            <DropdownMenuItem
                                data-testid={`${testid}-add-paste`}
                                onSelect={() => setPasteOpen(true)}
                                className="cursor-pointer flex-col items-start gap-0.5 py-2"
                            >
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <ClipboardPaste size={14} />
                                    Paste notes
                                </div>
                                <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                    Pasted or typed notes — auto-mapped
                                    to scenes &amp; characters.
                                </div>
                            </DropdownMenuItem>
                            ) : null}
                            {/* Iter-156G — Upload + Link are gated on
                                `isOwner` (which is the write-capable
                                flag, i.e. Owner / Admin / Contributor).
                                Bulk paste remains Admin-only above. */}
                            <DropdownMenuItem
                                data-testid={`${testid}-add-upload`}
                                onSelect={() => setUploadOpen(true)}
                                className="cursor-pointer flex-col items-start gap-0.5 py-2"
                            >
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <Upload size={14} />
                                    Upload document
                                </div>
                                <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                    File upload (PDF, DOCX, image) —
                                    stored as reference only.
                                </div>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid={`${testid}-add-link`}
                                onSelect={() => setLinkOpen(true)}
                                className="cursor-pointer flex-col items-start gap-0.5 py-2"
                            >
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <LinkIcon size={14} />
                                    Add external link
                                </div>
                                <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                    Google Sheets, Numbers, Excel Online —
                                    stays a reference, never embedded.
                                </div>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : null}
            </div>
            <p className="text-[11px] text-neutral-400 mb-2.5 leading-relaxed">
                Notes are reference only. They do not change scenes or characters.
            </p>

            <h3 className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
                Text notes
            </h3>
            <SupervisorNotesList
                key={`notes-${notesRefreshKey}`}
                projectId={projectId}
                sceneId={sceneId}
                characterId={characterId}
                isOwner={canAdmin}
                showSceneRef={showSceneRef}
                showCharacterRef={showCharacterRef}
                emptyHint={emptyHint}
                testidPrefix={`${testid}-list`}
                onChange={() => setNotesRefreshKey((v) => v + 1)}
            />

            <h3 className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5 mt-4 flex items-center gap-1.5">
                <FileText size={10} /> Documents
            </h3>
            <ChangeDocumentsTimeline
                key={`docs-${docsRefreshKey}`}
                projectId={projectId}
                sceneId={sceneId}
                characterId={characterId}
                isOwner={canAdmin}
                testidPrefix={`${testid}-docs`}
                emptyHint="No uploaded documents for this view."
                onChange={() => setDocsRefreshKey((v) => v + 1)}
            />

            <AddSupervisorNoteDialog
                open={addNoteOpen}
                onOpenChange={setAddNoteOpen}
                scenes={scenes}
                characters={characters}
                presetSceneId={sceneId || null}
                presetCharacterId={characterId || null}
                lockScene={Boolean(sceneId)}
                lockCharacter={Boolean(characterId)}
                onCreate={createNote}
            />
            <SupervisorNotesImportDialog
                projectId={projectId}
                open={pasteOpen}
                onOpenChange={setPasteOpen}
                onImported={() => setNotesRefreshKey((v) => v + 1)}
            />
            <ChangeDocumentUploadDialog
                projectId={projectId}
                open={uploadOpen}
                onOpenChange={setUploadOpen}
                scenes={scenes}
                characters={characters}
                presetSceneId={sceneId || null}
                presetCharacterId={characterId || null}
                onUploaded={() => setDocsRefreshKey((v) => v + 1)}
            />
            {/* Iter-156E — external-link parity. Same docs timeline,
                auto-attached to the contextual scene/character. */}
            <AddExternalLinkDialog
                projectId={projectId}
                open={linkOpen}
                onOpenChange={setLinkOpen}
                presetSceneId={sceneId || null}
                presetCharacterId={characterId || null}
                onCreated={() => setDocsRefreshKey((v) => v + 1)}
            />
        </section>
    );
}
